import { HttpCacheManager } from '@ngneat/cashew';

import { Injectable, inject } from '@angular/core';

import { OperationsChartsAPI } from '@services/API/trusts/operations/charts.service';
import { FILTERS_CACHE_KEY } from '@services/API/trusts/operations/filters/filters.service';
import { OperationsAPI } from '@services/API/trusts/operations/operation/operation.service';
import { LenderState } from '@services/state/lender-state/lender.state';
import { OperationsState } from '@services/state/operation-state/operation.state';

/**
 * Owns everything the shell keeps for the facilities catalogue, so it can be
 * dropped from one place.
 *
 * LEN-2587: a facility created from the companies remote used to stay invisible
 * in Portfolio Monitoring -> Facilities until logout, because the remote cannot
 * reach the shell's caches or state. The remote now calls
 * `window.invalidateFacilities`, which lands here.
 */
@Injectable({ providedIn: 'root' })
export class FacilitiesCache {
  private cacheManager = inject(HttpCacheManager);
  private operationsChartsAPI = inject(OperationsChartsAPI);
  private operationsAPI = inject(OperationsAPI);
  private operationsState = inject(OperationsState);
  private lenderState = inject(LenderState);

  /**
   * Drops the cached catalogue (charts pages and operations list) and the
   * operations state, without refetching anything.
   */
  dropCatalogue() {
    // Not `delete(cacheKey)`: since LEN-2049 the catalogue is stored one entry
    // per page (`operations-1-50`, `operations-2-50`, ...) and nothing at all
    // lives under the bare `'operations'` key, so that call dropped nothing and
    // the pre-edit catalogue survived the whole TTL.
    this.operationsChartsAPI.clearCache(this.cacheManager);
    // The charts key is not enough. Since LEN-2062 the operations list is
    // cached too, and operationGuard short-circuits on OperationsState, so a
    // facility edit would otherwise keep serving pre-edit master_admin,
    // available_topics and activeOperation() on the detail pages.
    // The bucket, not the key: since LEN-2158 each operations fetch is cached
    // under its own `urlWithParams`, so deleting the bare prefix would match
    // nothing and leave the pre-edit catalogue serving for the rest of the TTL.
    this.cacheManager.delete(this.operationsAPI.cacheBucket);
    this.operationsState.clear();
  }

  /**
   * Everything {@link dropCatalogue} drops, plus the cached filter options and
   * the lenders list. Nothing is fetched here: the Facilities page provides its
   * own `FilterSelectsState`, which refetches the filters on every visit, and
   * the dropped `filters` cache entry makes the next `LenderState` load fresh.
   */
  invalidate() {
    this.dropCatalogue();
    // `LenderState` reads the same `/operations/filters` response through the
    // HTTP cache; without dropping it, the next lenders load would be served
    // the pre-creation list for the rest of the TTL.
    this.cacheManager.delete(FILTERS_CACHE_KEY);
    // `setFromAPI` is guarded per user, so without this the General Overview
    // would keep the lenders it loaded before the new facility existed.
    this.lenderState.invalidate();
  }
}
